import API_BASE_URL from './config';
import React, { useState, useEffect, useRef } from 'react';

function MyInventory({ playerId }) {
  const [items, setItems] = useState([]);
  const [dfgDollars, setDfgDollars] = useState(0);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [dfgTransfer, setDfgTransfer] = useState({ username: '', amount: '' });
  const [itemTransfer, setItemTransfer] = useState({ username: '', itemId: '', quantity: '', search: '' });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!playerId) {
      setItems([]);
      setDfgDollars(0);
      setLoading(false);
      return;
    }
    const fetchInventory = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/my-inventory/${playerId}`);
        if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
        const data = await res.json();
        setItems((data.items || []).filter(item => item.quantity > 0));
        setDfgDollars(data.dfgDollars || 0);
      } catch (error) {
        setMessage('Failed to load inventory: ' + error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchInventory();
  }, [playerId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDollars = (amount) => {
    return amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const transferDfg = async () => {
    const { username, amount } = dfgTransfer;
    if (!username || !amount) {
      setMessage('Enter username and amount');
      return;
    }
    const confirmMessage = `Are you sure you want to transfer ${amount} DFG$ to ${username}? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;

    try {
      const res = await fetch(`${API_BASE_URL}/transfer-dfg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, targetUsername: username, amount: parseInt(amount) })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setDfgDollars(dfgDollars - parseInt(amount));
      setDfgTransfer({ username: '', amount: '' });
      setMessage(data.message);
    } catch (error) {
      setMessage('Transfer failed: ' + error.message);
    }
  };

  const transferItem = async () => {
    const { username, itemId, quantity } = itemTransfer;
    if (!username || !itemId || !quantity) {
      setMessage('Enter username, item, and quantity');
      return;
    }
    const item = items.find(i => i.itemId === itemId);
    if (!item || item.quantity < quantity) {
      setMessage('Insufficient quantity or item not found');
      return;
    }
    const confirmMessage = `Are you sure you want to transfer ${quantity} ${item.name} to ${username}? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;

    try {
      const res = await fetch(`${API_BASE_URL}/transfer-item`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerId, targetUsername: username, itemId, quantity: parseInt(quantity) })
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setItems(items.map(i => 
        i.itemId === itemId ? { ...i, quantity: i.quantity - quantity } : i
      ).filter(i => i.quantity > 0));
      setItemTransfer({ username: '', itemId: '', quantity: '', search: '' });
      setIsDropdownOpen(false);
      setMessage(data.message);
    } catch (error) {
      setMessage('Transfer failed: ' + error.message);
    }
  };

  const filteredItems = items.filter(item => 
    item.name.toLowerCase().includes(itemTransfer.search.toLowerCase())
  );

  const handleItemSelect = (itemId, name) => {
    setItemTransfer({ ...itemTransfer, itemId, search: name });
    setIsDropdownOpen(false);
  };

  if (!playerId) return <p style={{ textAlign: 'center', color: '#888' }}>Please log in to view your inventory.</p>;
  if (loading) return <p style={{ textAlign: 'center', color: '#888' }}>Loading inventory...</p>;

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <h1 style={{ color: '#1E88E5', marginBottom: '20px', textAlign: 'center' }}>My Inventory</h1>
      <p style={{ textAlign: 'center', marginBottom: '20px' }}>
        <img 
          src="https://images-wixmp-ed30a86b8c4ca887773594c2.wixmp.com/f/e278b58f-e271-405b-8060-c4679e13d416/djf7cy6-d917ff6c-ae63-4d32-9cdd-72d194240679.png?token=eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJ1cm46YXBwOjdlMGQxODg5ODIyNjQzNzNhNWYwZDQxNWVhMGQyNmUwIiwiaXNzIjoidXJuOmFwcDo3ZTBkMTg4OTgyMjY0MzczYTVmMGQ0MTVlYTBkMjZlMCIsIm9iaiI6W1t7InBhdGgiOiJcL2ZcL2UyNzhiNThmLWUyNzEtNDA1Yi04MDYwLWM0Njc5ZTEzZDQxNlwvZGpmN2N5Ni1kOTE3ZmY2Yy1hZTYzLTRkMzItOWNkZC03MmQxOTQyNDA2NzkucG5nIn1dXSwiYXVkIjpbInVybjpzZXJ2aWNlOmZpbGUuZG93bmxvYWQiXX0.T8u4BbInQZyL-a4Ponxzeh0d1PKwywI0ZRuCwYZXfN4" 
          alt="DFG$ Coin" 
          style={{ width: '50px', height: '50px', verticalAlign: 'middle', marginRight: '10px' }} 
        />
        <strong style={{ color: '#1E88E5', fontSize: '1.2em' }}>DFG$ Balance:</strong> <span style={{ color: '#333' }}>{formatDollars(dfgDollars)}</span>
      </p>
      {message && (
        <div style={{
          backgroundColor: '#BBDEFB',
          padding: '10px',
          marginBottom: '20px',
          borderRadius: '5px',
          color: '#333',
          fontWeight: 'bold',
          textAlign: 'center'
        }}>
          {message}
        </div>
      )}

      <h2 style={{ color: '#1E88E5', margin: '20px 0 10px', textAlign: 'center' }}>Items</h2>
      {items.length > 0 ? (
        <div style={{
          backgroundColor: '#f9f9f9',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
          border: '1px solid #BBDEFB'
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center' }}>
            {items.map(item => (
              <div
                key={item.itemId}
                style={{
                  textAlign: 'center',
                  width: '200px',
                  padding: '15px',
                  backgroundColor: '#fff',
                  borderRadius: '5px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  transition: 'transform 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
              >
                {item.artUrl && (
                  <img src={item.artUrl} alt={item.name} style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '8px' }} />
                )}
                <p style={{ margin: '10px 0', fontSize: '16px', color: '#333', fontWeight: 'bold' }}>{item.name} <span style={{ color: '#1976D2' }}>(x{item.quantity})</span></p>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <p style={{ textAlign: 'center', color: '#888', fontSize: '16px' }}>No items in your inventory yet!</p>
      )}

      <h2 style={{ color: '#1E88E5', margin: '20px 0 10px', textAlign: 'center' }}>Transfer DFG$</h2>
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="Username"
          value={dfgTransfer.username}
          onChange={e => setDfgTransfer({ ...dfgTransfer, username: e.target.value })}
          style={{ width: '120px', padding: '5px', marginRight: '10px', borderRadius: '5px', border: '1px solid #ccc' }}
        />
        <input
          type="number"
          placeholder="Amount"
          value={dfgTransfer.amount}
          onChange={e => setDfgTransfer({ ...dfgTransfer, amount: e.target.value })}
          min="1"
          max={dfgDollars}
          style={{ width: '100px', padding: '5px', marginRight: '10px', borderRadius: '5px', border: '1px solid #ccc' }}
        />
        <button
          onClick={transferDfg}
          style={{
            padding: '8px 15px',
            backgroundColor: '#1E88E5',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            transition: 'background-color 0.3s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#1E88E5'}
        >
          Transfer DFG$
        </button>
      </div>

      <h2 style={{ color: '#1E88E5', margin: '20px 0 10px', textAlign: 'center' }}>Transfer Items</h2>
      <div style={{ textAlign: 'center' }} ref={dropdownRef}>
        <input
          type="text"
          placeholder="Username"
          value={itemTransfer.username}
          onChange={e => setItemTransfer({ ...itemTransfer, username: e.target.value })}
          style={{ width: '120px', padding: '5px', marginRight: '10px', borderRadius: '5px', border: '1px solid #ccc' }}
        />
        <input
          type="text"
          placeholder="Search and select item"
          value={itemTransfer.search}
          onChange={e => {
            setItemTransfer({ ...itemTransfer, search: e.target.value });
            setIsDropdownOpen(true);
          }}
          onFocus={() => setIsDropdownOpen(true)}
          style={{ width: '150px', padding: '5px', marginRight: '10px', borderRadius: '5px', border: '1px solid #ccc' }}
        />
        {isDropdownOpen && filteredItems.length > 0 && (
          <div
            style={{
              position: 'absolute',
              backgroundColor: '#fff',
              border: '1px solid #BBDEFB',
              borderRadius: '5px',
              maxHeight: '150px',
              overflowY: 'auto',
              width: '150px',
              zIndex: 1000,
              boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
            }}
          >
            {filteredItems.map(item => (
              <div
                key={item.itemId}
                onClick={() => handleItemSelect(item.itemId, item.name)}
                style={{
                  padding: '5px 10px',
                  cursor: 'pointer',
                  backgroundColor: item.itemId === itemTransfer.itemId ? '#E6F0FA' : '#fff',
                  color: '#333',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#BBDEFB'}
                onMouseOut={(e) => e.target.style.backgroundColor = item.itemId === itemTransfer.itemId ? '#E6F0FA' : '#fff'}
              >
                {item.name} (x{item.quantity})
              </div>
            ))}
          </div>
        )}
        <input
          type="number"
          placeholder="Quantity"
          value={itemTransfer.quantity}
          onChange={e => setItemTransfer({ ...itemTransfer, quantity: e.target.value })}
          min="1"
          max={items.find(i => i.itemId === itemTransfer.itemId)?.quantity || 0}
          style={{ width: '100px', padding: '5px', marginRight: '10px', borderRadius: '5px', border: '1px solid #ccc' }}
        />
        <button
          onClick={transferItem}
          style={{
            padding: '8px 15px',
            backgroundColor: '#1E88E5',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            transition: 'background-color 0.3s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#1E88E5'}
        >
          Transfer Item
        </button>
      </div>
    </div>
  );
}

export default MyInventory;